import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useContacts, useDeleteContact, useBulkDeleteContacts, useUpdateContact } from '../../hooks/useContacts';
import { Modal } from '../ui/Modal';
import { ContactForm } from './ContactForm';
import { Contact } from '../../types';
import { TestBadge } from '../ui/TestBadge';

interface ContactListProps {
  companyId?: string;
}

type SortDir = 'asc' | 'desc';

export function ContactList({ companyId }: ContactListProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [editTarget, setEditTarget] = useState<Contact | null>(null);

  // Mehrfachauswahl
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const [singleDeleteError, setSingleDeleteError] = useState<string | null>(null);

  const limit = 25;

  const { data, isLoading, error, refetch } = useContacts({
    page, limit,
    search: search || undefined,
    company_id: companyId,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIcon = (col: string) => {
    if (sortBy !== col) return <span style={{ opacity: 0.3, fontSize: '10px', marginLeft: '4px' }}>↕</span>;
    return <span style={{ fontSize: '10px', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thStyle = (col: string): React.CSSProperties => ({
    padding: '10px 12px', textAlign: 'left', fontWeight: 500,
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    color: sortBy === col ? 'var(--text)' : undefined,
  });

  const { mutate: deleteContact, isPending: deleting } = useDeleteContact();
  const { mutate: bulkDelete, isPending: bulkDeleting } = useBulkDeleteContacts();
  const updateContactMutation = useUpdateContact();

  const handleRolleChange = (contact: Contact, rolle: 'hauptansprechpartner' | 'postempfaenger' | 'keine') => {
    updateContactMutation.mutate({
      id: contact.id,
      input: {
        is_primary_contact: rolle === 'hauptansprechpartner',
        is_postempfaenger: rolle === 'postempfaenger',
      },
    });
  };

  const contacts: Contact[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  // ── Checkbox-Logik ─────────────────────────────────────
  const allChecked = contacts.length > 0 && contacts.every(c => checkedIds.has(c.id));
  const someChecked = contacts.some(c => checkedIds.has(c.id));

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(prev => {
        const next = new Set(prev);
        contacts.forEach(c => next.delete(c.id));
        return next;
      });
    } else {
      setCheckedIds(prev => {
        const next = new Set(prev);
        contacts.forEach(c => next.add(c.id));
        return next;
      });
    }
  };

  const toggleOne = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // ── Einzel-Löschen ─────────────────────────────────────
  const confirmDelete = () => {
    if (!deleteTarget) return;
    setSingleDeleteError(null);
    deleteContact(deleteTarget.id, {
      onSuccess: () => { setDeleteTarget(null); refetch(); },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
        setSingleDeleteError(msg ?? 'Löschen fehlgeschlagen.');
      },
    });
  };

  // ── Mehrfach-Löschen ───────────────────────────────────
  const confirmBulkDelete = () => {
    setBulkDeleteError(null);
    bulkDelete([...checkedIds], {
      onSuccess: () => {
        setCheckedIds(new Set());
        setShowBulkDeleteModal(false);
        refetch();
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
        setBulkDeleteError(msg ?? 'Löschen fehlgeschlagen.');
      },
    });
  };

  const inputStyle: React.CSSProperties = {
    padding: '7px 12px', border: '1px solid var(--border2)',
    borderRadius: 'var(--radius)', fontSize: '13px',
    background: 'var(--surface)', color: 'var(--text)',
  };

  const checkedCount = checkedIds.size;

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Kontakt suchen..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ ...inputStyle, flex: 1, minWidth: '180px' }}
        />
        {checkedCount > 0 && (
          <button
            onClick={() => { setBulkDeleteError(null); setShowBulkDeleteModal(true); }}
            style={{
              padding: '7px 16px', background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius)', fontSize: '13px',
              fontWeight: 500, cursor: 'pointer',
            }}
          >
            🗑 {checkedCount} löschen
          </button>
        )}
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '7px 16px', background: checkedCount > 0 ? 'var(--surface)' : 'var(--accent)',
            color: checkedCount > 0 ? 'var(--text2)' : '#fff',
            border: checkedCount > 0 ? '1px solid var(--border2)' : 'none',
            borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
          }}
        >
          + Neuer Kontakt
        </button>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--accent)' }}>Fehler beim Laden.</div>
        ) : contacts.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>
            {search ? 'Keine Treffer.' : 'Noch keine Kontakte.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface2)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)' }}>
                {/* Alle-auswählen Checkbox */}
                <th style={{ padding: '10px 12px', width: '36px' }}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={toggleAll}
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    title={allChecked ? 'Alle abwählen' : 'Alle auswählen'}
                  />
                </th>
                <th style={thStyle('last_name')} onClick={() => handleSort('last_name')}>
                  Name{sortIcon('last_name')}
                </th>
                {!companyId && (
                  <th style={thStyle('company_name')} onClick={() => handleSort('company_name')}>
                    Firma{sortIcon('company_name')}
                  </th>
                )}
                <th style={thStyle('job_title')} onClick={() => handleSort('job_title')}>
                  Position{sortIcon('job_title')}
                </th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>Rolle</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>Tags</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 500 }}></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c: Contact, idx: number) => {
                const isChecked = checkedIds.has(c.id);
                return (
                  <tr
                    key={c.id}
                    onClick={() => setSelectedContact(c)}
                    style={{
                      borderTop: idx > 0 ? '1px solid var(--border)' : undefined,
                      cursor: 'pointer',
                      background: isChecked ? 'var(--accent-light)' : '',
                    }}
                    onMouseEnter={e => { if (!isChecked) e.currentTarget.style.background = 'var(--surface2)'; }}
                    onMouseLeave={e => { if (!isChecked) e.currentTarget.style.background = ''; }}
                  >
                    {/* Checkbox-Zelle */}
                    <td style={{ padding: '11px 12px' }} onClick={e => toggleOne(c.id, e)}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {}}
                        style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '11px 12px' }}>
                      <div style={{ fontWeight: 500, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {c.display_name}
                        <TestBadge show={c.is_test_data} />
                      </div>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                        {c.updated_source === 'ai' && c.last_ai_check_at && (
                          <span style={{ fontSize: '10px', color: 'var(--text3)' }}>
                            🤖 KI · {new Date(c.last_ai_check_at).toLocaleDateString('de-DE')}
                          </span>
                        )}
                        {c.updated_source === 'manual' && c.updated_by_name && c.updated_by_name !== 'Manuell' && (
                          <span style={{ fontSize: '10px', color: 'var(--text3)' }}>
                            ✎ {c.updated_by_name}
                          </span>
                        )}
                      </div>
                    </td>
                    {!companyId && (
                      <td style={{ padding: '11px 12px', fontSize: '12px', color: 'var(--text2)' }}>
                        {c.company_name ?? '—'}
                      </td>
                    )}
                    <td style={{ padding: '11px 12px', fontSize: '12px', color: 'var(--text2)' }}>
                      {c.job_title ?? '—'}
                    </td>
                    <td style={{ padding: '11px 12px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {([
                          { value: 'hauptansprechpartner', label: 'Haupt-AP' },
                          { value: 'postempfaenger', label: 'Post' },
                          { value: 'keine', label: 'Keine' },
                        ] as const).map(opt => {
                          const current = c.is_primary_contact
                            ? 'hauptansprechpartner'
                            : c.is_postempfaenger
                            ? 'postempfaenger'
                            : 'keine';
                          return (
                            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <input
                                type="radio"
                                name={`rolle_${c.id}`}
                                value={opt.value}
                                checked={current === opt.value}
                                onChange={() => handleRolleChange(c, opt.value)}
                                style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '11px', color: 'var(--text2)' }}>{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </td>
                    <td style={{ padding: '11px 12px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {(c.tags_array ?? []).filter(Boolean).slice(0, 3).map(tag => (
                          <span key={tag} style={{
                            padding: '1px 7px', background: 'var(--surface2)',
                            borderRadius: '20px', fontSize: '11px', color: 'var(--text2)',
                          }}>{tag.trim()}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '11px 12px', textAlign: 'right', color: 'var(--text3)', fontSize: '12px' }}>
                      →
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', fontSize: '13px', color: 'var(--text2)' }}>
          <span>{total} Kontakte gesamt</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '6px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px' }}>
              ← Zurück
            </button>
            <span style={{ padding: '7px 12px', color: 'var(--text3)' }}>{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              style={{ padding: '6px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', background: 'var(--surface)', cursor: 'pointer', fontSize: '13px' }}>
              Weiter →
            </button>
          </div>
        </div>
      )}

      {/* Modal: Neuer Kontakt */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Neuer Kontakt" size="md">
        <ContactForm
          companyId={companyId}
          onSuccess={() => { setShowCreateModal(false); refetch(); }}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Modal: Kontakt Detail */}
      <Modal isOpen={!!selectedContact && !editTarget} onClose={() => setSelectedContact(null)} title={selectedContact?.display_name ?? ''} size="md">
        {selectedContact && (
          <div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                onClick={() => { setEditTarget(selectedContact); setSelectedContact(null); }}
                style={{ padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text2)', cursor: 'pointer' }}
              >
                Bearbeiten
              </button>
              <button
                onClick={() => { setSingleDeleteError(null); setDeleteTarget(selectedContact); setSelectedContact(null); }}
                style={{ padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text3)', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
              >
                Löschen
              </button>
            </div>
            <div style={{ display: 'grid', gap: '10px', fontSize: '13px' }}>
              {selectedContact.job_title && <div><span style={{ color: 'var(--text3)', fontSize: '11px', textTransform: 'uppercase' }}>Position</span><div>{selectedContact.job_title}</div></div>}
              {selectedContact.email && <div><span style={{ color: 'var(--text3)', fontSize: '11px', textTransform: 'uppercase' }}>E-Mail</span><div>{selectedContact.email}</div></div>}
              {selectedContact.phone && <div><span style={{ color: 'var(--text3)', fontSize: '11px', textTransform: 'uppercase' }}>Telefon</span><div>{selectedContact.phone}</div></div>}
              {selectedContact.is_primary_contact && <div style={{ color: 'var(--accent)', fontSize: '12px', fontWeight: 500 }}>⭐ Haupt-Ansprechpartner</div>}
              {selectedContact.is_postempfaenger && <div style={{ color: 'var(--info, #0077cc)', fontSize: '12px', fontWeight: 500 }}>✉ Postempfänger</div>}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: Kontakt bearbeiten */}
      <Modal isOpen={!!editTarget} onClose={() => setEditTarget(null)} title="Kontakt bearbeiten" size="md">
        {editTarget && (
          <ContactForm
            initial={editTarget}
            companyId={companyId}
            onSuccess={() => {
              setEditTarget(null);
              void queryClient.invalidateQueries({ queryKey: ['contacts'] });
              void queryClient.invalidateQueries({ queryKey: ['company'] });
              void queryClient.invalidateQueries({ queryKey: ['companies'] });
              refetch();
            }}
            onCancel={() => setEditTarget(null)}
          />
        )}
      </Modal>

      {/* Modal: Einzel-Löschen */}
      <Modal isOpen={!!deleteTarget} onClose={() => { setDeleteTarget(null); setSingleDeleteError(null); }} title="Kontakt löschen" size="sm">
        <div style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '16px' }}>
          <strong>{deleteTarget?.display_name}</strong> wirklich löschen?
          <br /><span style={{ color: 'var(--text3)', fontSize: '12px' }}>Diese Aktion kann nicht rückgängig gemacht werden.</span>
        </div>
        {singleDeleteError && (
          <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '16px', whiteSpace: 'pre-line' }}>
            ⚠️ {singleDeleteError}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={() => { setDeleteTarget(null); setSingleDeleteError(null); }}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
            Abbrechen
          </button>
          {!singleDeleteError && (
            <button onClick={confirmDelete} disabled={deleting}
              style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
              {deleting ? 'Löschen...' : 'Löschen'}
            </button>
          )}
        </div>
      </Modal>

      {/* Modal: Mehrfach-Löschen */}
      <Modal isOpen={showBulkDeleteModal} onClose={() => { setShowBulkDeleteModal(false); setBulkDeleteError(null); }} title={`${checkedCount} Kontakte löschen`} size="sm">
        {!bulkDeleteError ? (
          <div style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '20px' }}>
            Wirklich <strong>{checkedCount} Kontakte</strong> löschen?
            <br /><span style={{ color: 'var(--text3)', fontSize: '12px' }}>Diese Aktion kann nicht rückgängig gemacht werden.</span>
          </div>
        ) : (
          <div style={{ padding: '12px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '16px', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
            ⚠️ {bulkDeleteError}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={() => { setShowBulkDeleteModal(false); setBulkDeleteError(null); }}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
            Abbrechen
          </button>
          {!bulkDeleteError && (
            <button onClick={confirmBulkDelete} disabled={bulkDeleting}
              style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
              {bulkDeleting ? 'Löschen...' : `${checkedCount} löschen`}
            </button>
          )}
        </div>
      </Modal>
    </div>
  );
}
